import { useState, useMemo, useEffect, useCallback } from 'react';
import styles from './OS.module.css';
import OSDrawer from './OSDrawer';
import { listar, criar, atualizar, COLECOES } from '../../services/firestore';
import { usePermissao } from '../../hooks/usePermissao';

// ====================================================================
// OS - Bloco 5.1
// Cliente vem da colecao "clientes" (razaoSocial/nomeFantasia).
// Realizado por produto = soma das movimentacoes SAIDA EFETIVADA com a OS,
// menos DEVOLUCAO EFETIVADA.
// ====================================================================

const STATUS_CLASS = {
  Aberta: 'aberta',
  'Em execução': 'execucao',
  Concluída: 'concluida',
};

const STATUS_TAG = {
  Aberta: 'stAberta',
  'Em execução': 'stExecucao',
  Concluída: 'stConcluida',
};

// Nome do cliente para exibicao em selects/cards (razaoSocial > nomeFantasia)
function nomeDoCliente(c) {
  if (!c) return '';
  return (c.razaoSocial && c.razaoSocial.trim()) ||
    (c.nomeFantasia && c.nomeFantasia.trim()) ||
    c.nome || '';
}

function calcularProgresso(materiais) {
  const comPrev = materiais.filter((m) => m.previsto && m.previsto > 0);
  if (comPrev.length === 0) return null;
  const totalPrev = comPrev.reduce((s, m) => s + m.previsto, 0);
  const totalReal = comPrev.reduce((s, m) => s + (m.realizado || 0), 0);
  if (totalPrev === 0) return null;
  return Math.min(Math.round((totalReal / totalPrev) * 100), 100);
}

function temNaoPrevisto(materiais) {
  return materiais.some((m) => m.naoPrevisto);
}

// Retorna true se algum produto previsto ultrapassou o limite (default 120%)
function temAlertaConsumo(materiais, limitePct) {
  if (!limitePct || limitePct <= 0) return false;
  return materiais.some(
    (m) => m.previsto && m.previsto > 0 && (m.realizado / m.previsto) * 100 >= limitePct
  );
}

function classeBarra(previsto, realizado) {
  if (!previsto || previsto === 0) return 'barExcd';
  const pct = realizado / previsto;
  if (pct >= 1) return 'barOk';
  return 'barParcial';
}

export default function OS() {
  // Permissoes via hook central
  const { pode } = usePermissao();
  const podeCriar = pode('criarOS');
  const podeEditar = pode('editarOS');

  const [ordens, setOrdens] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [produtos, setProdutos] = useState([]);
  const [movimentacoes, setMovimentacoes] = useState([]);
  const [alertaConsumoOS, setAlertaConsumoOS] = useState(120);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');

  const [busca, setBusca] = useState('');
  const [filtroCliente, setFiltroCliente] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');

  const [drawerAberto, setDrawerAberto] = useState(false);
  const [osEditando, setOsEditando] = useState(null);
  const [salvando, setSalvando] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro('');
    try {
      const [os, cls, prods, movs, params] = await Promise.all([
        listar(COLECOES.ORDENS_SERVICO),
        listar(COLECOES.CLIENTES),
        listar(COLECOES.PRODUTOS, { ordenarPor: 'cor' }),
        listar(COLECOES.MOVIMENTACOES),
        listar(COLECOES.PARAMETROS),
      ]);
      setOrdens(os);
      // Mapeia clientes para ter sempre "nome" preenchido a partir de razaoSocial/nomeFantasia
      const clientesMapeados = cls
        .filter((c) => c.ativo !== false)
        .map((c) => ({ ...c, nome: nomeDoCliente(c) }))
        .filter((c) => c.nome && c.nome.trim().length > 0)
        .sort((a, b) => a.nome.localeCompare(b.nome));
      setClientes(clientesMapeados);
      setProdutos(prods.filter((p) => p.ativo !== false));
      setMovimentacoes(movs);
      if (params.length > 0 && Number(params[0].alertaConsumoOS) > 0) {
        setAlertaConsumoOS(Number(params[0].alertaConsumoOS));
      }
    } catch (e) {
      setErro(e.message || 'Erro ao carregar OS.');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const mapProdutos = useMemo(() => {
    const m = {};
    produtos.forEach((p) => { m[p.id] = p; });
    return m;
  }, [produtos]);

  const ordensComRealizado = useMemo(() => {
    return ordens.map((os) => {
      const movsDaOS = movimentacoes.filter(
        (m) =>
          m.status === 'EFETIVADA' &&
          (m.tipo === 'SAIDA' || m.tipo === 'DEVOLUCAO') &&
          (m.os || '').trim() === (os.numero || '').trim()
      );

      const realPorProduto = {};
      movsDaOS.forEach((mov) => {
        (mov.itens || []).forEach((it) => {
          const sinal = mov.tipo === 'SAIDA' ? +1 : -1;
          realPorProduto[it.produtoId] =
            (realPorProduto[it.produtoId] || 0) + sinal * (it.qtd || 0);
        });
      });

      const previstos = (os.materiais || []).map((m) => ({
        ...m,
        realizado: realPorProduto[m.produtoId] || 0,
      }));

      const previstoIds = new Set(previstos.map((m) => m.produtoId));
      const naoPrevistos = Object.entries(realPorProduto)
        .filter(([pid, qtd]) => !previstoIds.has(pid) && qtd > 0)
        .map(([pid, qtd]) => ({
          produtoId: pid,
          previsto: null,
          realizado: qtd,
          naoPrevisto: true,
        }));

      return {
        ...os,
        materiais: [...previstos, ...naoPrevistos],
      };
    });
  }, [ordens, movimentacoes]);

  const ordensFiltradas = useMemo(() => {
    // Se o usuario nao aplicou nenhum filtro/busca, esconde as Concluidas por padrao.
    // Quando ele busca ou filtra, mostra todas (incluindo Concluidas).
    const semFiltro = !busca && !filtroCliente && !filtroStatus;

    return ordensComRealizado.filter((os) => {
      if (semFiltro && os.status === 'Concluída') return false;

      if (busca) {
        const q = busca.toLowerCase();
        const match =
          (os.numero || '').toLowerCase().includes(q) ||
          (os.clienteNome || '').toLowerCase().includes(q) ||
          (os.area || '').toLowerCase().includes(q);
        if (!match) return false;
      }
      if (filtroCliente && os.clienteId !== filtroCliente) return false;
      if (filtroStatus && os.status !== filtroStatus) return false;
      return true;
    });
  }, [ordensComRealizado, busca, filtroCliente, filtroStatus]);

  const cont = {
    aberta: ordens.filter((o) => o.status === 'Aberta').length,
    execucao: ordens.filter((o) => o.status === 'Em execução').length,
    concluida: ordens.filter((o) => o.status === 'Concluída').length,
  };

  function abrirNova() {
    if (!podeCriar) return;
    setOsEditando(null);
    setDrawerAberto(true);
  }
  function abrirEditar(os) {
    setOsEditando(os);
    setDrawerAberto(true);
  }
  function fecharDrawer() {
    setDrawerAberto(false);
    setOsEditando(null);
  }

  async function salvar(dados) {
    // Defesa em profundidade: bloqueia escrita no banco se o perfil nao puder
    if (osEditando && !podeEditar) {
      setErro('Você não tem permissão para editar OS.');
      return;
    }
    if (!osEditando && !podeCriar) {
      setErro('Você não tem permissão para criar OS.');
      return;
    }
    setSalvando(true);
    setErro('');
    try {
      if (osEditando) {
        await atualizar(COLECOES.ORDENS_SERVICO, osEditando.id, dados);
      } else {
        await criar(COLECOES.ORDENS_SERVICO, dados);
      }
      fecharDrawer();
      await carregar();
    } catch (e) {
      setErro(e.message || 'Erro ao salvar OS.');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <>
      <div className={styles.topbar}>
        <div>
          <div className={styles.pageTitle}>Ordens de Serviço</div>
          <div className={styles.pageSub}>
            Materiais previstos × realizados por produto
          </div>
        </div>
        {podeCriar && (
          <button className={styles.btnNovo} onClick={abrirNova}>
            + Nova OS
          </button>
        )}
      </div>

      {erro && (
        <div
          style={{
            background: '#fdecea',
            color: '#b51013',
            border: '1px solid #f5c6cb',
            borderRadius: 8,
            padding: '10px 14px',
            margin: '0 0 14px',
            fontSize: 14,
          }}
        >
          {erro}
        </div>
      )}

      <div className={styles.toolbar}>
        <input
          className={styles.toolbarInput}
          placeholder="🔍 Buscar OS, cliente, área..."
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
        />
        <select
          className={styles.toolbarSelect}
          value={filtroCliente}
          onChange={(e) => setFiltroCliente(e.target.value)}
        >
          <option value="">Todos os clientes</option>
          {clientes.map((c) => (
            <option key={c.id} value={c.id}>
              {c.nome}
            </option>
          ))}
        </select>
        <select
          className={styles.toolbarSelect}
          value={filtroStatus}
          onChange={(e) => setFiltroStatus(e.target.value)}
        >
          <option value="">Todos os status</option>
          <option value="Aberta">Aberta</option>
          <option value="Em execução">Em execução</option>
          <option value="Concluída">Concluída</option>
        </select>
      </div>

      <div className={styles.cardsHead}>
        <span>
          <strong>{ordensFiltradas.length}</strong> de {ordens.length} ordens (
          {cont.aberta} abertas · {cont.execucao} em execução · {cont.concluida} concluídas
          {!busca && !filtroCliente && !filtroStatus && cont.concluida > 0 && (
            <em style={{ marginLeft: 6, color: '#888' }}>
              · concluídas ocultas, use a busca ou o filtro de status para ver
            </em>
          )}
          )
        </span>
        <span className={styles.hint}>Clique no card para abrir</span>
      </div>

      {carregando ? (
        <div className={styles.semResultados}>Carregando ordens de serviço...</div>
      ) : ordensFiltradas.length === 0 ? (
        <div className={styles.semResultados}>
          {ordens.length === 0
            ? 'Nenhuma OS cadastrada. Clique em "+ Nova OS" para começar.'
            : 'Nenhuma OS encontrada com os filtros aplicados.'}
        </div>
      ) : (
        <div className={styles.cardsGrid}>
          {ordensFiltradas.map((os) => {
            const progresso = calcularProgresso(os.materiais);
            const naoPrev = temNaoPrevisto(os.materiais);
            const alertaConsumo = temAlertaConsumo(os.materiais, alertaConsumoOS);
            const materiaisVisiveis = (os.materiais || []).slice(0, 2);
            const restantes = (os.materiais || []).length - 2;

            return (
              <div
                key={os.id}
                className={`${styles.osCard} ${
                  styles[STATUS_CLASS[os.status] || 'aberta']
                }`}
                style={alertaConsumo ? {
                  borderColor: '#E89B1B',
                  borderWidth: 2,
                  boxShadow: '0 0 0 1px #E89B1B inset',
                } : undefined}
                onClick={() => abrirEditar(os)}
              >
                <div className={styles.oscHead}>
                  <div>
                    <div className={styles.oscNum}>{os.numero}</div>
                    <div className={styles.oscCliente}>
                      <strong>{os.clienteNome}</strong>
                      {os.contato ? ` · ${os.contato}` : ''}
                    </div>
                    <span className={styles.oscArea}>{os.area}</span>
                  </div>
                  <span
                    className={`${styles.statusTag} ${
                      styles[STATUS_TAG[os.status] || 'stAberta']
                    }`}
                  >
                    {os.status}
                  </span>
                </div>

                <div className={styles.oscDesc}>{os.descricao}</div>

                <div className={styles.oscMat}>
                  {materiaisVisiveis.map((m, idx) => {
                    const p = mapProdutos[m.produtoId];
                    const pct =
                      m.previsto && m.previsto > 0
                        ? Math.min((m.realizado / m.previsto) * 100, 100)
                        : m.naoPrevisto
                        ? 100
                        : 0;
                    const barClass = m.naoPrevisto
                      ? 'barExcd'
                      : classeBarra(m.previsto, m.realizado);
                    return (
                      <div key={idx} className={styles.oscMatRow}>
                        <div className={styles.oscMatNome}>
                          {p ? (p.cor || '—') : m.produtoId}
                        </div>
                        <div className={styles.oscMatBarWrap}>
                          <div className={styles.oscMatBar}>
                            <div
                              className={`${styles.oscMatBarFill} ${styles[barClass]}`}
                              style={{
                                width: `${pct}%`,
                                background:
                                  pct === 0 && !m.naoPrevisto ? '#ddd' : undefined,
                              }}
                            />
                          </div>
                        </div>
                        <div className={styles.oscMatVal}>
                          {m.realizado}/{m.previsto ?? '—'}
                        </div>
                      </div>
                    );
                  })}
                  {restantes > 0 && (
                    <div className={styles.oscMais}>
                      +{restantes} produto{restantes > 1 ? 's' : ''}
                    </div>
                  )}
                  {(os.materiais || []).length === 0 && (
                    <div className={styles.oscMais}>Sem materiais previstos</div>
                  )}
                </div>

                <div className={styles.oscFoot}>
                  {alertaConsumo ? (
                    <span
                      style={{
                        background: '#FFF4D6',
                        color: '#7A5200',
                        border: '1px solid #E89B1B',
                        borderRadius: 99,
                        padding: '2px 8px',
                        fontSize: 11,
                        fontWeight: 600,
                      }}
                    >
                      ⚠ atingiu {alertaConsumoOS}%
                    </span>
                  ) : naoPrev ? (
                    <span className={styles.tagNaoPrev}>contém não previsto</span>
                  ) : progresso !== null ? (
                    <span className={styles.tagProgresso}>
                      {progresso}% concluído
                    </span>
                  ) : (
                    <span className={styles.tagSemPrev}>sem previstos</span>
                  )}
                  <span className={styles.oscDocs}>
                    📄 {(os.documentos || []).length} doc.
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {drawerAberto && (
        <OSDrawer
          os={osEditando}
          clientes={clientes}
          produtos={produtos}
          onSalvar={salvar}
          onFechar={fecharDrawer}
          salvando={salvando}
          somenteLeitura={osEditando ? !podeEditar : !podeCriar}
        />
      )}
    </>
  );
}